import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, AppBar, Toolbar, Typography, IconButton, useMediaQuery, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import Categories from './components/Categories';
import WallLite from './components/WallLite';
import PictureLight from './components/PictureLight';
import Zoomer from './components/Zoomer';
import Hanging from './components/Hanging';
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import Login from './components/Login';
import Logo from './components/Logo';
import SearchResults from './components/SearchResults';
import SearchBar from './components/SearchBar';
import Cart from './components/Cart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { cartService } from './services/cart';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb', // Modern blue
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#ec4899', // Modern pink
      light: '#f472b6',
      dark: '#db2777',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1e293b',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          borderRadius: '1rem',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
          padding: '0.5rem 1.5rem',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
  },
});

// Separate AppContent component that uses navigation
const AppContent = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const cart = cartService.getCart();
      setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));
    };
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    return () => window.removeEventListener('storage', updateCartCount);
  }, []);

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<MainLayout navigate={navigate} isMobile={isMobile} cartCount={cartCount} />} />
      </Routes>
    </Box>
  );
};

// Main layout component with header
const MainLayout = ({ navigate, isMobile, cartCount }: { navigate: any, isMobile: boolean, cartCount: number }) => {
  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          <IconButton
            edge="start"
            color="primary"
            aria-label="home"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <HomeIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Logo width={isMobile ? 120 : 150} />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="primary" onClick={() => navigate('/cart')}>
            <Badge badgeContent={cartCount} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Container 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3 },
          maxWidth: { xl: 'xl', lg: 'lg' }
        }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/walllite" element={<WallLite />} />
          <Route path="/picturelight" element={<PictureLight />} />
          <Route path="/zoomer" element={<Zoomer />} />
          <Route path="/hanging" element={<Hanging />} />
          <Route path="/category/:categoryId" element={<ProductList />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/cart" element={<Cart />} />
        </Routes>
      </Container>
    </>
  );
};

// Home component
const Home = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const [searchQuery, setSearchQuery] = useState('');
  // const navigate = useNavigate();

  // const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setSearchQuery(event.target.value);
  // };

  // const handleSearchSubmit = () => {
  //   // Navigate to ProductList with search query
  //   // Assuming you have a route to handle search results
  //   navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
  // };

  return (
    <Box>
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 4, sm: 6 },
        mt: { xs: 1, sm: 2 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Logo width={isMobile ? 200 : 250} />
        </Box>
        <Typography 
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ 
            maxWidth: '600px', 
            mx: 'auto', 
            mb: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 0 },
            fontSize: {
              xs: '1rem',
              sm: '1.125rem'
            }
          }}
        >
          Browse our collection of premium lighting solutions
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <SearchBar />
        </Box>
      </Box>
      <Categories />
    </Box>
  );
};

// Main App component
function App() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Router>
  );
}

export default App;
